import { createContext, useContext, useId, useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

// The reference "AI thought chain" (shadcn + Radix + motion) ported onto spark's stack for the Studio
// prototype: the same structure and API, drawn with spark's colour roles and type ladder, a native button
// with aria-expanded instead of Radix Collapsible, CSS instead of motion. Kept in the Studio until it
// earns a place among the design primitives.
export type ThoughtChainStatus = "done" | "active" | "pending";

const StepContext = createContext<{
  open: boolean;
  toggle: () => void;
  status: ThoughtChainStatus;
  contentId: string;
  collapsible: boolean;
} | null>(null);

function useStep() {
  const step = useContext(StepContext);
  if (!step) throw new Error("ThoughtChain parts belong inside a ThoughtChainStep");
  return step;
}

const STATUS_STYLES: Record<ThoughtChainStatus, { label: string; line: string }> = {
  done: { label: "text-ink-muted", line: "bg-ink/15" },
  active: { label: "text-ink", line: "bg-accent" },
  pending: { label: "text-ink-muted", line: "bg-ink/10" },
};

function StatusIcon({ status }: { status: ThoughtChainStatus }) {
  if (status === "done") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-ink">
        <svg viewBox="0 0 24 24" className="size-3 text-ink" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
    );
  }
  if (status === "active") {
    return (
      <svg viewBox="0 0 24 24" className="size-5 animate-spin stroke-accent-decoration motion-reduce:animate-none" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="size-5 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function ThoughtChain({ children }: { children: ReactNode }) {
  return <div>{children}</div>;
}

export function ThoughtChainStep({
  children,
  status = "pending",
  defaultOpen = true,
  collapsible = true,
}: {
  children: ReactNode;
  status?: ThoughtChainStatus;
  defaultOpen?: boolean;
  // A step without content has nothing to fold: no chevron, always "open".
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  return (
    <StepContext.Provider value={{ open, toggle: () => setOpen((value) => !value), status, contentId, collapsible }}>
      <div className="flex gap-3.5">
        <div className="flex shrink-0 flex-col items-center">
          <span className="mt-0.5">
            <StatusIcon status={status} />
          </span>
          <span className={`mt-1.5 min-h-5 w-0.5 flex-1 rounded-sm ${STATUS_STYLES[status].line}`} />
        </div>
        <div className="min-w-0 flex-1 pb-2">{children}</div>
      </div>
    </StepContext.Provider>
  );
}

export function ThoughtChainTrigger({ children }: { children: ReactNode }) {
  const { open, toggle, status, contentId, collapsible } = useStep();
  const label = (
    <span className={`text-smycles-body font-semibold tracking-tight ${STATUS_STYLES[status].label}`}>
      {status === "active" && typeof children === "string" ? <span className="studio-shimmer">{children}</span> : children}
    </span>
  );
  const badge = status === "active" ? (
    <span className="rounded-full px-2 py-0.5 text-smycles-micro font-semibold text-ink-accent ring-1 ring-accent-decoration/40">
      <FormattedMessage id="studio.thought.inProgress" defaultMessage="In progress" />
    </span>
  ) : null;
  if (!collapsible) return <div className="flex flex-wrap items-center gap-1.5">{label}{badge}</div>;
  return (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={open}
      aria-controls={contentId}
      className="flex cursor-pointer select-none flex-wrap items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
    >
      {label}
      <span className={`flex items-center text-ink-muted transition-transform duration-200 ease-in-out motion-reduce:transition-none ${open ? "rotate-180" : ""}`}>
        <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
      {badge}
    </button>
  );
}

export function ThoughtChainContent({ children }: { children: ReactNode }) {
  const { open, contentId } = useStep();
  // Height animates through the grid-rows trick, so no measuring and no animation library.
  return (
    <div
      id={contentId}
      inert={!open}
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out motion-reduce:transition-none ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
    >
      <div className="overflow-hidden">
        <div className="mt-1 pl-0.5">{children}</div>
      </div>
    </div>
  );
}

export function ThoughtChainItem({ children }: { children: ReactNode }) {
  return (
    <div className="studio-item-in flex items-start gap-2 py-1">
      <span className="mt-2 size-1 shrink-0 rounded-full bg-ink/25" />
      <span className="text-smycles-meta leading-relaxed text-ink-muted">{children}</span>
    </div>
  );
}
